import { AgentDto, TrialDto } from "@growth/dto";
import { Badge, Button, cn, ConfirmDialog, Field, Input, Textarea } from "@growth/ui";
import { FlaskConical, History, LoaderCircle, Pencil, RotateCcw, Save, X } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ErrorText, PageHeader, selectClass } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { formatRelative } from "../lib/format";
import {
  useAgents,
  useAgentVersions,
  useResetInstructions,
  useSaveInstructions,
  useStartTrial,
  useTrial,
} from "../lib/research";

const STATUS: Record<
  AgentDto["status"],
  { label: string; tone: "success" | "accent" | "neutral" }
> = {
  built: { label: "Working", tone: "success" },
  next: { label: "Next", tone: "accent" },
  planned: { label: "Planned", tone: "neutral" },
  later: { label: "Later", tone: "neutral" },
};

/**
 * Every agent, what it reads, and the instructions it runs with for this brand. Owners rewrite
 * them, try a change on a sample first, and can always go back to an earlier version.
 */
export const AgentsPage = () => {
  const { brand } = useActive();
  const agents = useAgents();
  const [params, setParams] = useSearchParams();
  const selectedKey = params.get("agent") ?? "researcher";
  const selected = agents.data?.find((agent) => agent.key === selectedKey) ?? agents.data?.[0];

  return (
    <section className="max-w-6xl space-y-6">
      <PageHeader eyebrow={brand?.name ?? ""} title="Agents">
        Each agent starts with our instructions. Owners can rewrite them for this brand, try the
        change on a sample, and go back to any earlier version. The rules about claims, invented
        details and approval always apply.
      </PageHeader>
      {agents.isError && <ErrorText>{apiErrorMessage(agents.error)}</ErrorText>}
      {agents.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {agents.data && selected && (
        <div className="grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <nav aria-label="Agents" className="space-y-1">
            {agents.data.map((agent) => (
              <button
                key={agent.key}
                type="button"
                aria-pressed={agent.key === selected.key}
                onClick={() => setParams({ agent: agent.key }, { replace: true })}
                className={cn(
                  "flex w-full flex-col gap-1 rounded-lg border px-3 py-2.5 text-left",
                  agent.key === selected.key
                    ? "border-accent bg-white"
                    : "border-transparent hover:border-line hover:bg-white",
                )}
              >
                <span className="flex items-center justify-between gap-2 text-sm font-medium text-ink">
                  {agent.name}
                  <Badge tone={STATUS[agent.status].tone}>{STATUS[agent.status].label}</Badge>
                </span>
                <span className="text-xs text-muted">
                  {agent.edited
                    ? `Edited for ${brand?.name} · v${agent.version}`
                    : "Using the default"}
                  {agent.updateAvailable && " · default updated"}
                </span>
              </button>
            ))}
          </nav>
          <AgentDetail key={`${selected.key}-${selected.version}`} agent={selected} />
        </div>
      )}
    </section>
  );
};

const AgentDetail = ({ agent }: { agent: AgentDto }) => {
  const { atLeast } = useActive();
  const isOwner = atLeast("OWNER");
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(agent.instructions);
  const [note, setNote] = useState("");
  const [compare, setCompare] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [trying, setTrying] = useState(false);
  const save = useSaveInstructions(agent.key);
  const reset = useResetInstructions(agent.key);
  const dirty = text.trim() !== agent.instructions.trim();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(
      { body: text, note, baseVersion: agent.version },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <div className="min-w-0 space-y-5 rounded-xl border border-line bg-white p-5">
      <div className="space-y-2">
        <h2 className="font-serif text-2xl text-ink">{agent.name}</h2>
        <p className="text-sm leading-relaxed text-ink/80">{agent.job}</p>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted">Reads:</span>
          {agent.reads.map((item) => (
            <span key={item} className="rounded-full bg-paper px-2.5 py-0.5 text-xs text-ink">
              {item}
            </span>
          ))}
        </div>
      </div>

      {agent.updateAvailable && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
          We improved the default instructions since this brand's version was written.
          <Button variant="secondary" onClick={() => setCompare(true)}>
            Compare
          </Button>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Instructions</h3>
          <span className="text-xs text-muted">
            {agent.edited
              ? `Version ${agent.version}${agent.updatedBy ? ` by ${agent.updatedBy.name ?? agent.updatedBy.email}` : ""}${agent.updatedAt ? `, ${formatRelative(agent.updatedAt)}` : ""}${agent.note ? ` · "${agent.note}"` : ""}`
              : "Our default"}
          </span>
        </div>

        {editing ? (
          <form onSubmit={submit} className="space-y-3">
            <Textarea
              aria-label="Instructions"
              value={text}
              onChange={(event) => setText(event.target.value)}
              rows={12}
              maxLength={8000}
              className="font-mono text-[13px] leading-relaxed"
            />
            <Field label="What changed (optional)">
              {(props) => (
                <Input
                  {...props}
                  maxLength={200}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              )}
            </Field>
            {save.isError && <ErrorText>{apiErrorMessage(save.error)}</ErrorText>}
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={!dirty || save.isPending}>
                {save.isPending ? (
                  <LoaderCircle className="size-4 animate-spin" />
                ) : (
                  <Save className="size-4" />
                )}
                Save as version {agent.version + 1}
              </Button>
              {agent.canTry && (
                <Button type="button" variant="secondary" onClick={() => setTrying(true)}>
                  <FlaskConical className="size-4" /> Try it first
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setEditing(false);
                  setText(agent.instructions);
                }}
              >
                <X className="size-4" /> Cancel
              </Button>
            </div>
          </form>
        ) : (
          <pre className="whitespace-pre-wrap rounded-lg border border-line bg-paper/40 p-4 font-mono text-[13px] leading-relaxed text-ink">
            {agent.instructions}
          </pre>
        )}

        {!agent.editable && <p className="text-sm text-muted">{agent.lockedNote}</p>}
        {agent.editable && !isOwner && (
          <p className="text-sm text-muted">Only owners can change an agent's instructions.</p>
        )}
        {agent.editable && isOwner && !editing && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setEditing(true)}>
              <Pencil className="size-4" /> Edit
            </Button>
            {agent.canTry && (
              <Button variant="secondary" onClick={() => setTrying((open) => !open)}>
                <FlaskConical className="size-4" /> Try it on a sample
              </Button>
            )}
            {agent.edited && (
              <Button variant="secondary" onClick={() => setCompare((open) => !open)}>
                Compare with the default
              </Button>
            )}
            {agent.edited && (
              <ConfirmDialog
                trigger={
                  <Button variant="ghost">
                    <RotateCcw className="size-4" /> Reset to the default
                  </Button>
                }
                title="Go back to the default instructions?"
                description="This brand's version stays in the history, so you can bring it back."
                confirmLabel="Reset"
                onConfirm={() => reset.mutate(agent.version)}
              />
            )}
          </div>
        )}
        {reset.isError && <ErrorText>{apiErrorMessage(reset.error)}</ErrorText>}
      </div>

      {compare && (
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <p className="text-xs font-semibold text-muted">This brand's version</p>
            <pre className="whitespace-pre-wrap rounded-lg border border-line p-3 font-mono text-xs leading-relaxed">
              {agent.instructions}
            </pre>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-semibold text-muted">
              Our default (updated {agent.defaultVersion})
            </p>
            <pre className="whitespace-pre-wrap rounded-lg border border-line p-3 font-mono text-xs leading-relaxed">
              {agent.defaultInstructions}
            </pre>
          </div>
        </div>
      )}

      {trying && agent.canTry && (
        <TrialPanel
          agent={agent}
          instructions={editing ? text : agent.instructions}
          onClose={() => setTrying(false)}
        />
      )}

      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Always applied (can't be edited)</h3>
        <ul className="list-disc space-y-1 pl-5 text-sm text-ink/80">
          {agent.lockedRules.map((rule) => (
            <li key={rule}>{rule}</li>
          ))}
        </ul>
      </div>

      {agent.editable && (
        <div className="space-y-2">
          <Button variant="ghost" onClick={() => setShowHistory((open) => !open)}>
            <History className="size-4" /> {showHistory ? "Hide history" : "History"}
          </Button>
          {showHistory && (
            <Versions
              agent={agent}
              onRestore={
                isOwner
                  ? (body) => {
                      setText(body);
                      setEditing(true);
                    }
                  : undefined
              }
            />
          )}
        </div>
      )}
    </div>
  );
};

const Versions = ({
  agent,
  onRestore,
}: {
  agent: AgentDto;
  onRestore?: (body: string) => void;
}) => {
  const versions = useAgentVersions(agent.key, true);
  if (versions.isPending) return <LoaderCircle className="size-4 animate-spin text-muted" />;
  if (versions.isError) return <ErrorText>{apiErrorMessage(versions.error)}</ErrorText>;
  if (!versions.data.length)
    return (
      <p className="text-sm text-muted">No changes yet: this agent has always used the default.</p>
    );
  return (
    <ul className="divide-y divide-line rounded-lg border border-line text-sm">
      {versions.data.map((version) => (
        <li
          key={version.version}
          className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
        >
          <span>
            <strong>v{version.version}</strong> ·{" "}
            {version.body === null ? "Back to the default" : version.note || "Edited"}
            <span className="text-muted">
              {" "}
              · {version.createdBy?.name ?? version.createdBy?.email ?? "someone"},{" "}
              {formatRelative(version.createdAt)}
            </span>
          </span>
          {onRestore && version.body !== null && version.version !== agent.version && (
            <Button variant="ghost" onClick={() => onRestore(version.body!)}>
              Use this version
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
};

/** The sample each agent needs: an idea and channel, a draft to judge, or a topic. */
const TrialPanel = ({
  agent,
  instructions,
  onClose,
}: {
  agent: AgentDto;
  instructions: string;
  onClose: () => void;
}) => {
  const start = useStartTrial(agent.key);
  const [trialId, setTrialId] = useState<string | null>(null);
  const trial = useTrial(trialId);
  const channels =
    agent.key === "designer"
      ? [
          ["instagram", "Instagram carousel"],
          ["linkedin", "LinkedIn document"],
        ]
      : [
          ["linkedin", "LinkedIn post"],
          ["reddit", "Reddit answer"],
        ];
  const [sample, setSample] = useState({
    channel: channels[0]![0]!,
    idea: "",
    draft: "",
    topic: "",
  });
  const running =
    Boolean(trialId) && (!trial.data || ["queued", "running"].includes(trial.data.status));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const input =
      agent.key === "researcher"
        ? { topic: sample.topic }
        : agent.key === "critic"
          ? { channel: sample.channel as "linkedin", draft: sample.draft }
          : { channel: sample.channel as "linkedin", idea: sample.idea };
    start.mutate(
      { instructions, sample: input },
      { onSuccess: (started) => setTrialId(started.id) },
    );
  };

  return (
    <div className="space-y-4 rounded-xl border border-blue-200 bg-blue-50/40 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">Try it on a sample</h3>
          <p className="text-xs text-muted">
            Runs {agent.name.toLowerCase()} twice on the same sample: once with the instructions
            above and once with our default. Nothing is saved. Costs a few cents
            {agent.key === "researcher" ? " (it searches the web)" : ""}.
          </p>
        </div>
        <Button variant="ghost" aria-label="Close" onClick={onClose}>
          <X className="size-4" />
        </Button>
      </div>
      <form
        onSubmit={submit}
        className="grid gap-3 sm:grid-cols-[12rem_minmax(0,1fr)_auto] sm:items-end"
      >
        {agent.key === "researcher" ? (
          <Field label="Topic to research" className="sm:col-span-2">
            {(props) => (
              <Input
                {...props}
                required
                maxLength={300}
                value={sample.topic}
                onChange={(event) => setSample({ ...sample, topic: event.target.value })}
              />
            )}
          </Field>
        ) : (
          <>
            <Field label="Channel">
              {(props) => (
                <select
                  {...props}
                  className={selectClass}
                  value={sample.channel}
                  onChange={(event) => setSample({ ...sample, channel: event.target.value })}
                >
                  {channels.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            {agent.key === "critic" ? (
              <Field label="A draft to judge">
                {(props) => (
                  <Textarea
                    {...props}
                    required
                    rows={3}
                    maxLength={5000}
                    value={sample.draft}
                    onChange={(event) => setSample({ ...sample, draft: event.target.value })}
                  />
                )}
              </Field>
            ) : (
              <Field label="Idea">
                {(props) => (
                  <Input
                    {...props}
                    required
                    maxLength={500}
                    value={sample.idea}
                    onChange={(event) => setSample({ ...sample, idea: event.target.value })}
                  />
                )}
              </Field>
            )}
          </>
        )}
        <Button type="submit" disabled={start.isPending || running}>
          {start.isPending || running ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : (
            <FlaskConical className="size-4" />
          )}
          Run
        </Button>
      </form>
      {start.isError && <ErrorText>{apiErrorMessage(start.error)}</ErrorText>}
      {running && <p className="text-sm text-muted">Running both versions…</p>}
      {trial.data?.status === "failed" && <ErrorText>{trial.data.error}</ErrorText>}
      {trial.data?.status === "succeeded" && <TrialResult trial={trial.data} />}
    </div>
  );
};

type Output =
  | { kind: "text"; body: string; firstComment: string | null }
  | { kind: "slides"; title: string; slides: string[]; body: string }
  | { kind: "review"; scores: Record<string, number>; mustFix: string[]; verdict: string }
  | {
      kind: "ideas";
      summary: string;
      ideas: { title: string; why: string; sources: { title: string; url: string }[] }[];
    };

const TrialResult = ({ trial }: { trial: TrialDto }) => (
  <div className="space-y-2">
    <div className="grid gap-3 md:grid-cols-2">
      {(
        [
          ["Your version", trial.proposed],
          ["Our default", trial.default],
        ] as const
      ).map(([label, output]) => (
        <div key={label} className="space-y-2 rounded-lg border border-line bg-white p-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted">{label}</p>
          <OutputView output={output as Output} />
        </div>
      ))}
    </div>
    <p className="text-xs text-muted">Cost about US${trial.costUsd.toFixed(3)}</p>
  </div>
);

const OutputView = ({ output }: { output: Output | null }) => {
  if (!output) return <p className="text-sm text-muted">No answer.</p>;
  if (output.kind === "text")
    return (
      <div className="space-y-2 text-sm">
        <p className="whitespace-pre-line leading-relaxed">{output.body}</p>
        {output.firstComment && (
          <p className="text-xs text-muted">First comment: {output.firstComment}</p>
        )}
      </div>
    );
  if (output.kind === "slides")
    return (
      <div className="space-y-2 text-sm">
        <p className="font-medium">{output.title}</p>
        <ol className="list-decimal space-y-1 pl-5 text-xs leading-relaxed">
          {output.slides.map((slide, index) => (
            <li key={index}>{slide}</li>
          ))}
        </ol>
        <p className="whitespace-pre-line text-xs text-muted">{output.body}</p>
      </div>
    );
  if (output.kind === "review")
    return (
      <div className="space-y-2 text-sm">
        <p className="text-xs">
          {Object.entries(output.scores)
            .map(([key, value]) => `${key} ${value}`)
            .join(" · ")}
        </p>
        <p className="italic text-muted">{output.verdict}</p>
        <ul className="list-disc space-y-1 pl-5 text-xs">
          {output.mustFix.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    );
  return (
    <div className="space-y-2 text-sm">
      <p className="text-muted">{output.summary}</p>
      {output.ideas.map((idea) => (
        <div key={idea.title}>
          <p className="font-medium">{idea.title}</p>
          <p className="text-xs">{idea.why}</p>
          {idea.sources.map((source) => (
            <a
              key={source.url}
              href={source.url}
              target="_blank"
              rel="noreferrer"
              className="mr-2 text-xs text-accent underline"
            >
              {source.title}
            </a>
          ))}
        </div>
      ))}
    </div>
  );
};
